import {Utils} from './Utils'
import {ArgValidator} from "./ArgValidator";
import {StringUtils} from "./StringUtils";
import {padEnd} from 'lodash';
//import BigJS = BigJsLibrary.BigJS;

var Big = require('big.js');

/**
 * @interface INumberFormatter - number formatter, will format number value to expected formatted string.
 */
export interface INumberFormatter {
    /**
     * @method validateFormat - validates input format string if its valid for current implementation of @INumberFormatter.
     * @param format - format string to be validated.
     */
    validateFormat(format: string): boolean;

    /**
     * @method formatValue - formats number value to required formatted string
     * @param value - number value to be formatted.
     * @param format - format string used for transformation from number to string.
     */
    formatValue(value: number, format: string): string;
}

/**
 * @class DefaultNumberFormatter - default implementation of number formatter.
 * @summary Allowed characters are: 0(zero) ,(comma) .(dot) where first character must be 0(zero)
 * @example 0,00 (formats 123.456 => 123,45)
 */
export class DefaultNumberFormatter implements INumberFormatter {
    validateFormat(format: string): boolean {
        ArgValidator.ensureStringIsNotNullOrEmpty(format, 'format');

        return /^[0]{1}(?:\.|,){0,1}[0]*$/gi.test(format) === true;
    }

    private formatIntegerPart(value: number): string {
        return value < 0
            ? '-' + Math.floor(Math.abs(value))
            : Math.floor(value) + '';
    }

    formatValue(value: number, format: string): string {
        ArgValidator.ensureIsNotNullOrUndefined(value, 'value');
        ArgValidator.ensureStringIsNotNullOrEmpty(format, 'format');

        var result = value + '';
        var decSeparatorMatch = /(\.|,)/gi.exec(format);
        var decSeparator = Utils.isNullOrUndefined(decSeparatorMatch) ? null : decSeparatorMatch[0];
        if (Utils.isNullOrUndefined(decSeparator)) {
            result = this.formatIntegerPart(value);
        }
        else {
            var valueFormat = format.split(decSeparator)[1];
            if (StringUtils.isNullOrEmpty(valueFormat)) {
                result = this.formatIntegerPart(value);
            } else {
                var values = (value + '').split(/(\.|,)/gi);
                var lastValue = values.length === 3 ? values[values.length - 1] : '';
                var decPart = valueFormat.length > lastValue.length
                    ? padEnd(lastValue, valueFormat.length, '0')
                    : lastValue.substr(0, valueFormat.length);
                result = values[0] + decSeparator + decPart;
            }
        }
        return result;
    }
}

var _defaultNumberFormatter = new DefaultNumberFormatter();

export class NumberUtils {
    public static get defaultNumberFormatter(): INumberFormatter {
        return _defaultNumberFormatter;
    }

    public static toNumber(value: string | number | boolean): number {
        var type = typeof value;

        if (type === 'string') {
            return Number(value);
        } else if (type === 'number') {
            return value as number;
        } else if (type === 'boolean') {
            return value === true ? 1 : 0;
        }

        return NaN;
    }

    public static tryParseNumber(value: any): { value: number, isValid: boolean } {
        var result = NumberUtils.toNumber(value);
        return {
            value: result,
            isValid: !isNaN(result)
        };
    }

    public static valueToNumber(value: string | number | boolean, fractionDigits?: number): string {
        var number = NumberUtils.toNumber(value);
        fractionDigits = Utils.valueOrDefault(fractionDigits, 0);
        // return number.toFixed(fractionDigits);
        return new Big(number).round(fractionDigits, 0).toFixed(fractionDigits).toString();
    }

    public static formatNumber(value: number, format: string, formatter?: INumberFormatter): string {
        ArgValidator.ensureIsNotNullOrUndefined(value, 'value');
        ArgValidator.ensureStringIsNotNullOrEmpty(format, 'format');
        formatter = Utils.valueOrDefault(formatter, NumberUtils.defaultNumberFormatter);

        if (!formatter.validateFormat(format)) {
            throw new Error(`Format '${format}' is not valid format!`);
        }

        return formatter.formatValue(value, format);
    }

    /**
     * @method add - Add two numbers and returns 'floating multiply safe' string result.
     * @param value1
     * @param value2
     * @returns {string}
     */
    public static add(value1: string | number, value2: string | number): string {
        return new Big(value1).plus(value2).toString();
    }

    /**
     * @method subtract - Subtract two numbers and returns 'floating multiply safe' string result.
     * @param value1
     * @param value2
     * @returns {string}
     */
    public static subtract(value1: string | number, value2: string | number): string {
        return new Big(value1).minus(value2).toString();
    }

    /**
     * @method multiply - Multiply two numbers and returns 'floating multiply safe' string result.
     * @param value1
     * @param value2
     * @returns {string}
     */
    public static multiply(value1: string | number, value2: string | number): string {
        return new Big(value1).times(value2).toString();
    }

    /**
     * @method devide - Devide two numbers and returns 'floating multiply safe' string result.
     * @param value1
     * @param value2
     * @returns {string}
     */
    public static devide(value1: string | number, value2: string | number): string {
        return new Big(value1).div(value2).toString();
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Utils/NumberUtils.ts
 **/